import { ONBOARDING_PAGES, TRACK_EVENTS } from "core/consts";
import { getQueryVariable, useIntParams } from "core/model/utils/urls";
import React, { ReactNode, useCallback, useContext, useState } from "react";
import {
  NavigateFunction,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import Stepper from "./components/Stepper";
import { OnboardingPageWrapper, OnboardingWrapper } from "./pages/shared";
import { STEPPER_STEPS, STEPS, Step, getStepperSteps } from "./steps";

function inStepper(step: Step): boolean {
  if (!step) return false;
  return (STEPPER_STEPS as Array<Step>).includes(step);
}
const goToStep =
  ({
    baseUrl,
    navigate,
    token,
  }: {
    baseUrl: string;
    navigate: NavigateFunction;
    token: string;
  }) =>
  (stepIndex?: number) => {
    const tokenVar = `?token=${token}`;

    if (!stepIndex) {
      return navigate(`${baseUrl}${tokenVar}`);
    }

    return navigate(`${baseUrl}/${STEPS[stepIndex]}${tokenVar}`);
  };

const resetPasswordParams = ({
  navigate,
  search,
}: {
  navigate: NavigateFunction;
  search: URLSearchParams;
}) => {
  search.delete("reset");
  search.delete("password_token");
  return navigate(
    {
      search: search.toString(),
    },
    { replace: true },
  );
};

type OnboardingContextType = {
  careproviderId: number;
  careproviderToken: string;
  currentStep: string;
  getFormInputValue: () => Record<string, any>;
  goTo: (step: Step) => void;
  isResetPasswordPage: boolean;
  next: () => void;
  passwordToken?: string;
  previous: () => void;
  reset: () => void;
  resetPasswordPage: () => void;
  saveFormInputValue: (inputValue: Record<string, any>) => void;
  steps: Step[];
};

const defaultState = {
  careproviderId: -1,
  careproviderToken: "",
  current: -1,
  currentStep: "",
  getFormInputValue: () => ({}),
  goTo: (step: Step) => step,
  isResetPasswordPage: false,
  next: () => {},
  previous: () => {},
  reset: () => {},
  resetPasswordPage: () => {},
  saveFormInputValue: (inputValue: Record<string, any>) => inputValue,
  setCurrent: (step: number) => step,
  steps: STEPS,
};

const OnboardingContext =
  React.createContext<OnboardingContextType>(defaultState);

/**
 *
 * @returns the OnboardingContext, including the tokens for Onboarding Password Reset Page
 */
export const useOnboarding = () => useContext(OnboardingContext);

export default function NewCareproviderOnboarding({
  children,
}: {
  children?: ReactNode;
}) {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { careproviderId } = useIntParams<{ careproviderId: number }>({
    careproviderId: -1,
  });
  const { step = ONBOARDING_PAGES.WELCOME } = useParams<{ step: Step }>();
  const currentStepIndex = STEPS.indexOf(step);
  const baseUrl = `/careprovider/newonboarding/${careproviderId}`;
  const token = getQueryVariable(location.search, "token") as string;
  const passwordToken = getQueryVariable(location.search, "password_token");
  const { trackEvent } = useTracking();
  const translations = useTranslations();

  const isResetPage = Boolean(search.get("reset"));

  const [formInputValues, setFormInputValues] =
    useState<Record<string, any>>(defaultState);

  const navigateTo = goToStep({
    baseUrl,
    navigate,
    token,
  });

  let showStepper = false;
  if (inStepper(step)) {
    showStepper = !(step == "password" && !passwordToken);
  }

  const currentStep = `(${translations.newOnboardingTwo.stepper.step} ${currentStepIndex}/5)`;

  return (
    <OnboardingContext.Provider
      value={{
        careproviderId,
        careproviderToken: token,
        currentStep,
        getFormInputValue: () => formInputValues[step],
        goTo: useCallback((nextStep: Step) => {
          const destination = STEPS.indexOf(nextStep);
          trackEvent({
            action: "goTo",
            distinct_id: careproviderId,
            from: STEPS[currentStepIndex],
            name: TRACK_EVENTS.NEW_ONBOARDING,
            to: STEPS[destination],
          });

          navigateTo(destination);
        }, []),
        isResetPasswordPage: isResetPage,
        next: () => {
          const destination = currentStepIndex + 1;
          trackEvent({
            action: "next",
            distinct_id: careproviderId,
            from: STEPS[currentStepIndex],
            name: TRACK_EVENTS.NEW_ONBOARDING,
            to: STEPS[destination],
          });
          navigateTo(destination);
        },
        passwordToken,
        previous: () => {
          const destination = currentStepIndex - 1;
          trackEvent({
            action: "back",
            distinct_id: careproviderId,
            from: STEPS[currentStepIndex],
            name: TRACK_EVENTS.NEW_ONBOARDING,
            to: STEPS[destination],
          });

          navigateTo(destination);
        },
        reset: () => navigateTo(),
        resetPasswordPage: () => {
          resetPasswordParams({ navigate, search });
        },
        saveFormInputValue: (inputValue: Record<string, any>) =>
          setFormInputValues({
            ...formInputValues,
            [step]: inputValue,
          }),
        steps: STEPS,
      }}
    >
      <OnboardingWrapper>
        <Stepper
          activeStep={step}
          showSteps={showStepper}
          steps={getStepperSteps(translations)}
        />
        <OnboardingPageWrapper>
          {children ? children : <Outlet />}
        </OnboardingPageWrapper>
      </OnboardingWrapper>
    </OnboardingContext.Provider>
  );
}
